import { Occassion } from 'types/reservations';
import { PaymentMethod } from './takeAway';
import { AdditionalFeedbackQuestion } from './additionalFeedback';
import { Descendant } from 'slate';
import SmsSettingsDocument from 'types/shared/documents/restaurants/settings/SmsSettingsDocument';
import { ConnectTo } from 'types/shared/helper/sia';
import { TicketRefundCalculation } from 'types/shared/documents/restaurants/event';
import {
  FAQSettingsDocument,
  MinimumConsumptionSettingsDocument,
  ReservationIntegrationSettingsDocument,
  ReservationSettingsDocument,
} from 'types/shared/documents/restaurants/settings';

export enum SettingsName {
  GENERAL = 'general',
  ADDRESS = 'address',
  SOCIAL_MEDIA = 'social-media',
  IDENTITY = 'stylesV02',
  INVOICE = 'invoice',
  RESERVATION = 'reservationsV02',
  FEEDBACK = 'feedback',
  RESERVATION_INTEGRATION = 'reservationPlugin',
  VOUCHER = 'voucher',
  TAKEAWAY = 'takeAway',
  PROFILE = 'profile',
  WEBSITE = 'website',
  LINKTREE = 'linkTree',
  GUEST_ORDERING = 'guestOrdering',
  SMS = 'sms',
  SIA = 'siaSettingsV02',
  MIN_CONSUMPTION = 'minimumConsumption',
  FAQ = 'faqs',
  TICKET_CANCELLATION = 'ticketCancellation',
}

export type GeneralSettings = {
  legalName: string;
  description?: string;
  restaurantName: string;
  email: string;
  phoneNumber: string;
  url: string;
};

export const DEFAULT_GENERAL_SETTINGS: GeneralSettings = {
  legalName: '',
  description: '',
  restaurantName: '',
  email: '',
  phoneNumber: '',
  url: '',
};

export type AddressSettings = {
  street: string;
  addressZusatz: string;
  city: string;
  zipCode: string;
  country: string;
  coordinates: { lat: number; lng: number };
  deliverySettings?: any; // @TODO
  state?: string;
};

export const DEFAULT_ADDRESS_SETTINGS: AddressSettings = {
  street: '',
  addressZusatz: '',
  city: '',
  zipCode: '',
  country: '',
  coordinates: { lat: 0, lng: 0 },
};

export type SocialMediaSettings = {
  facebook: string;
  google: string;
  instagram: string;
  tripAdvisor: string;
};

export const DEFAULT_SOCIAL_MEDIA_SETTINGS: SocialMediaSettings = {
  facebook: '',
  google: '',
  instagram: '',
  tripAdvisor: '',
};

export type IdentitySettings = {
  logo?: string;
  logoName?: string;
  logoId?: string;
  logoBrightness?: number;
  logoTransparent?: boolean;
  emailLogo?: string;
  emailLogoName?: string;
  emailLogoId?: string;
  emailLogoScale?: 'small' | 'medium' | 'large';
  emailFooterLogos?: string[];
  defaultImage?: string;
  defaultImageName?: string;
  defaultImageId?: string;
  colorPalette: {
    primaryColor: string;
    onPrimary: string;
    backgroundColor?: string;
    colorTheme?: 'light' | 'darkGrey' | 'midnight';
  };
};

export const DEFAULT_IDENTITY_SETTINGS: IdentitySettings = {
  colorPalette: {
    primaryColor: '#10CCB9',
    onPrimary: '#011311',
    backgroundColor: '#ffffff',
    colorTheme: 'light',
  },
};

export type InvoiceSettings = {
  name: string;
  vatId: string;
  email: string;
  phoneNumber: string;
  url: string;
  logo?: string;
  logoId?: string;
  useRestaurantAddress: boolean;
  address?: {
    street: string;
    addressZusatz: string;
    city: string;
    zipCode: string;
    country: string;
  };
  bankDetails: {
    name: string;
    iban: string;
    bic: string;
  };
};

export const DEFAULT_INVOICE_SETTINGS: InvoiceSettings = {
  name: '',
  vatId: '',
  email: '',
  phoneNumber: '',
  url: '',
  useRestaurantAddress: true,
  address: {
    street: '',
    addressZusatz: '',
    city: '',
    zipCode: '',
    country: '',
  },
  bankDetails: {
    name: '',
    iban: '',
    bic: '',
  },
};

export enum RESERVATION_EMAIL {
  NEVER = 0,
  DAY_OF_RESERVATION = 1,
  ALWAYS = 2,
}

export type ReservationSettings = ReservationSettingsDocument;

export const DEFAULT_RESERVATION_SETTINGS: ReservationSettings = {
  occassions: [],
  pendingTill: 0,
  maxGuestsOnWebsite: 12,
  hideResLengthInEmails: false,
  phoneNumberMandatory: true,
  addUserDetails: false,
  signatureRequired: false,
  customAttr: [],
  minTimeInAdvance: 1,
  maxDaysInAdvance: 0,
};

export type FeedbackSettings = {
  prefferedDestinations: ('tripAdvisor' | 'facebook' | 'google')[];
  texts: {
    highRatingText: Record<string, string>;
    lowRatingText: Record<string, string>;
  };
  additionalFeedback?: AdditionalFeedbackQuestion[];
  internalFeedbackForHighRatings?: boolean;
};

export const DEFAULT_FEEDBACK_TEXTS: Record<string, Record<string, string>> = {
  de: {
    highRatingText:
      'Wir würden uns freuen, wenn Sie sich eine Minute Zeit nehmen und Ihre Erfahrungen mit der Welt teilen. Das hilft uns und anderen Gästen ungemein. Herzlichen Dank!',
    lowRatingText:
      'Wir würden uns sehr über ehrliche und konstruktive Kritik freuen, um uns die Chance zu geben, uns in Zukunft zu verbessern. Vielen Dank!',
  },
  en: {
    highRatingText:
      'We would be happy if you take a minute and share your experience with the world. This helps us and other guests enormously. Thanks a lot!',
    lowRatingText:
      'We sincerely would appreciate your honest and constructive criticsm to give us the chance to improve ourselves in the future!',
  },
};

export const DEFAULT_FEEDBACK_SETTINGS: FeedbackSettings = {
  prefferedDestinations: ['google', 'tripAdvisor'],
  texts: {
    highRatingText: {
      de: '',
      en: '',
    },
    lowRatingText: {
      de: '',
      en: '',
    },
  },
  additionalFeedback: [],
};

export type ReservationIntegrationSettings =
  ReservationIntegrationSettingsDocument;

export const DEFAULT_RESERVATION_INTEGRATION_SETTINGS: ReservationIntegrationSettings =
  {
    styles: {
      primaryColor: '#10CCB9',
      onPrimaryColor: '#011311',
    },
    timeDisplay: 'dropdown', // For Future use,
    buttonText: '',
    disclaimer: {
      de: 'Bitte beachte, dass Reservierungen die nach 15 Minuten ohne Information nicht im Restaurant erscheinen, als "No-Show" markiert werden und dies zu einer fortwährenden Sperrung für zukünftige Reservierungen führen kann. Daher bitten wir dich, uns darüber zu informieren, sofern sich deine Pläne ändern sollten.',
      en: 'Please note that reservations are only valid for 15 minutes. Reservations that have not communicated their delay or no-show within this time will be automatically canceled. A so-called "no-show" is noted in our system and can lead to the name being blocked if it is repeated, so please let us know if your plans change. Thanks a lot!',
    },
  };

export type RestaurantUser = {
  admin: boolean;
  chefWaiter: boolean;
  createdAt: number;
  email: string;
  name: string;
  restaurant: string;
  role: string;
  tablet: boolean;
  waiter: boolean;
  id: string;
};

export type Invitation = {
  createdAt: number;
  name: string;
  status: 'success' | 'failed' | 'pending';
  role?: string;
  id: string;
};

export const DEFAULT_VOUCHER_SETTINGS: VouchersSettings = {
  voucherName: '',
  serviceFee: {
    max: null,
    min: null,
    type: 'euro',
    value: 0,
  },
  voucherPriceSettings: {
    max: null,
    min: 20,
    steps: 20,
  },
  buttons: [],
  designs: [],
};

export type VouchersSettings = {
  voucherName: string;
  serviceFee: {
    max: null | number; // currently unused
    min: null | number; // currently unused
    type: 'euro' | 'percentage';
    value: number;
  };
  voucherPriceSettings: {
    max: null | number;
    min: number;
    steps: number;
  };
  buttons: any[]; // handled by cloud function
  designs: any[]; // handled by cloud function
};

type TakeAwayShift = any; // @TODO

export const DEFAULT_TAKEAWAY_SETTINGS: TakeAwaySettings = {
  name: '',
  deliveryMethods: ['delivery', 'pickup'],
  deliverySettings: {
    minDistance: 0,
    maxDistance: 1800,
    type: 'duration',
  },
  fBPixel: '',
  minOrderValue: {
    delivery: 0,
    pickup: 0,
  },
  paymentMethods: {
    delivery: [PaymentMethod.SOFORT, PaymentMethod.CARD, PaymentMethod.SEPA],
    pickup: [PaymentMethod.SOFORT, PaymentMethod.CARD, PaymentMethod.SEPA],
  },
  shifts: [],
  takeAwayStyles: {
    categoryStyle: '1',
    headerColor: '#fff',
    headerType: 'color',
  },
};

export type TakeAwaySettings = {
  deliveryMethods: ('pickup' | 'delivery')[];
  deliverySettings: {
    minDistance?: number;
    maxDistance?: number;
    step?: {
      cost: number;
      value: number;
    };
    zipCodes?: {
      id: number;
      deliveryFee: number;
      values: string;
      minOrderValue: number;
    }[];
    type: 'distance' | 'duration' | 'zipCodes';
  };
  fBPixel?: string;
  minOrderValue: { delivery: number; pickup: number };
  name: string;
  paymentMethods: {
    delivery: PaymentMethod[];
    pickup: PaymentMethod[];
  };
  shifts: TakeAwayShift[];
  styles?: {};
  takeAwayStyles: {
    categoryStyle: '1' | '2' | '3';
    headerBackground?: string;
    headerColor?: string;
    headerType: 'color' | 'image';
    logo?: string;
    showImageOnMeal?: boolean;
  };
  currentWaitingTimes?: {
    delivery: string;
    pickup: string;
  };
};

export type OpenDay = { start: string; close: string; service?: string };

export type PictureObject = {
  url: string;
  thumbnails: {
    size: number;
    url: string;
    type: string;
  }[];
  description?: string;
  selected: boolean;
};

export type ProfileSettings = {
  title: string;
  description: string;
  formattedAddress: string;
  address?: {
    street: string;
    city: string;
    zipCode: string;
  };
  coords?: {
    lat: number;
    lng: number;
  };
  areaId: string;
  openingHours: (null | OpenDay)[];
  defaultOccassion?: string;
  permanentlyClosed?: boolean;
  contact: {
    phone: string;
    email: string;
    website: string;
    menu?: string;
  };
  socials: {
    instagram?: string;
    tripAdvisor?: string;
    facebook?: string;
  };
  pictures: (string | PictureObject)[];
  kitchens: string[];
  goodToKnow?: {
    parking?: string; // ?
    dressCode?: string; // ?
    paymentOptions?: string[]; // done
    priceRange?: string | null; // ? $-$$
    cuisine?: string; // ?
    chef?: string; // ?
    wheelChairAccess?: boolean; // done
    terrace?: boolean; // ?
    additionalInformations?: string; // done
    breakfast?: boolean;
    dinner?: boolean;
    kidsFriendly?: boolean;
  };
  mealIds?: string[];
  showPrice?: boolean;
  safetyMeasures?: {
    modal?: string;
    contactlessPayment?: boolean;
    staffWearsMasks?: boolean;
    disinfectantForGuests?: boolean;
    surfacesDisinfected?: boolean;
    guestCheckedBeforeEntry?: boolean;
    lightPassControl?: boolean;
    contactTracking?: boolean;
    reducedGuestNumber?: boolean;
    distanceBetweenTables?: boolean;
  };
};

export const DEFAULT_PROFILE_SETTINGS: ProfileSettings = {
  title: '',
  description: '',
  formattedAddress: '',
  openingHours: Array.from(Array(7), () => null),
  permanentlyClosed: false,
  contact: {
    phone: '',
    email: '',
    website: '',
  },
  socials: {},
  kitchens: [],
  pictures: [],
  areaId: '',
  goodToKnow: {},
};

export type WebsiteEvent = {
  id: string;
  eventName: string;
  dateAndTime: string;
  cost: string;
  link: string;
};

export type socialOptionsProps = {
  id: string;
  label: string;
};

export type navLinksProps = {
  id: string;
  title: string;
  linksTo: string;
};
export type websiteWeekDaysProps = {
  id: string;
  weekDays: number[];
  restaurantOpeningTimings: string;
  kitchenOpeningTimings: string;
};
export type WebsiteContactProps = {
  phoneNumber: string;
  address: string;
  email: string;
};
export type TemplateProps = {
  id: string;
  title: string;
  description: string;
  slug: string;
  image: string;
};
export type Website = {
  events: WebsiteEvent[];
  logo: string;
  socialVersion: string;
  showInHero: boolean;
  showInFooter: boolean;
  font: string;
  primaryColor: string;
  fontColor: string;
  backgroundColor: string;
  suggestions: string[];
  socialOptions: socialOptionsProps[];
  navLinks: navLinksProps[];
  websiteWeekDays: websiteWeekDaysProps[];
  websiteFooterMap: string;
  footerImage: string;
  impressum: Descendant[];
  impressumHero: string | (string | null)[] | null;
  datenschutz: Descendant[];
  datenschutzHero: string | (string | null)[] | null;
  agb: Descendant[];
  agbHero: string | (string | null)[] | null;
  websiteContact: WebsiteContactProps;
  templates: TemplateProps[];
  otherpages: TemplateProps[];
  domain: string;
};
export const DEFAULT_WEBSITE_SETTINGS: Website = {
  events: [
    {
      id: '1',
      eventName: '',
      dateAndTime: '',
      cost: '',
      link: '',
    },
  ],
  logo: '',
  socialVersion: 'V1',
  showInHero: false,
  showInFooter: false,
  socialOptions: [
    { id: 'V1', label: 'Version V1' },
    { id: 'V2', label: 'Version V2' },
  ],
  navLinks: [
    {
      id: '1',
      title: 'Home',
      linksTo: '/home',
    },
    {
      id: '2',
      title: 'Impressum',
      linksTo: '/impressum',
    },
  ],
  websiteWeekDays: [
    {
      id: '1',
      weekDays: [0, 1],
      restaurantOpeningTimings: '08:00 - 23:00',
      kitchenOpeningTimings: '12:00 - 19:00',
    },
    {
      id: '2',
      weekDays: [2, 5, 6],
      restaurantOpeningTimings: '08:00 - 23:00',
      kitchenOpeningTimings: '11:00 - 14:00',
    },
  ],
  websiteFooterMap: '',
  footerImage: '',
  websiteContact: {
    phoneNumber: '',
    address: '',
    email: '',
  },
  templates: [
    {
      id: 'Terms',
      title: 'Terms',
      description: 'This is a description for this template',
      slug: '',
      image: '',
      // onClick: () => console.log("Clicked Terms & conditions"),
    },
  ],
  impressum: [
    {
      children: [{ text: '' }],
    },
  ],
  impressumHero: '',
  datenschutz: [
    {
      children: [{ text: '' }],
    },
  ],
  datenschutzHero: '',
  agb: [
    {
      children: [{ text: '' }],
    },
  ],
  agbHero: '',
  font: 'Roboto',
  primaryColor: '#CCCCCC',
  fontColor: '#888888',
  backgroundColor: '#000000',
  suggestions: ['#EB9B43', '#CCCCCC', '#888888'],
  otherpages: [
    {
      id: 'Datenschutz',
      title: 'Datenschutz',
      description: 'This is a description for this template',
      slug: '',
      image: '',
      // onClick: () => console.log("Clicked Terms & conditions"),
    },
  ],
  domain: '',
};

export type Check = {
  id: string;
  title: string;
  severity?: 'error' | 'warning' | 'suggestion';
  button?: null | { text: string; link: string };
  skipable?: boolean;
  children?: Check[];
};

export type QuotaDocument = {
  monthId: string;
  counter: number;
  freeSeatings?: number;
  cancelations: number;
  canHaveMore?: boolean;
  pricePerExtraSeating?: number;
  done?: boolean;
  totalPrice?: number;
  firstSeatingAt?: string;
};

export type LinkTreeType = {
  restaurantId: string;
  restaurantName: string;
  phone?: string;
  openingHours: (OpenDay | null)[];
  logo: string;
  logoBrightness: number;
  logoTransparent: boolean;
  hero?: string;
  styles: {
    primaryColor: string;
    onPrimary: string;
    backgroundColor?: string;
    colorTheme?: 'light' | 'darkGrey' | 'midnight';
  };
  socials: {
    instagram?: string;
    tripAdvisor?: string;
    facebook?: string;
  };
  buttons: LinkTreeButtonType[];
  events?: LinkTreeEventType[];
};

export type LinkTreeButtonType = {
  icon?: string | undefined;
  text: Record<string, string>;
  link?: string | undefined;
  indivMenu?: string | undefined;
  action?:
    | 'reservation'
    | 'menu'
    | 'takeAway'
    | 'website'
    | 'platform'
    | 'individualMenu'
    | undefined;
};

export type LinkTreeEventType = {
  translations: Record<
    string,
    { title: string; description: string; button: string }
  >;
  link?: string;
  date?: string;
  time?: string;
  price?: string;
  imageId?: string;
  imageUrl?: string;
};

export const DEFAULT_LINKTREE_SETTINGS: LinkTreeType = {
  restaurantId: '',
  restaurantName: '',
  openingHours: [
    {
      start: '',
      close: '',
    },
  ],
  logo: '',
  logoBrightness: 0,
  logoTransparent: false,
  styles: {
    primaryColor: '',
    onPrimary: '',
  },
  socials: {},
  buttons: [
    {
      text: {
        en: '',
        de: '',
      },
    },
  ],
};

export enum GuestOrderingVariant {
  SPACE = 0,
  TABLE = 1,
  SEAT = 2,
  PICKUP = 3,
}

export enum GuestOrderingPaymentType {
  PAYMENT_ONLY = 0,
  NO_PAYMENT = 1,
  BOTH = 2,
}

export type PickUpSpot = {
  id: string;
  name: string;
  translations: Record<string, { title: string; description?: string }>;
  imageId: string | null;
  imageUrl?: string;
};

export interface GuestOrderingSettings {
  variant: GuestOrderingVariant;
  pickupSpots?: PickUpSpot[]; // only when variant is PICKUP
  minOrderValue: number; // minimum number is 10€
  paymentType: GuestOrderingPaymentType;
  callWaiter: boolean; // when true enables function to call for the waiter
  stations: string[];
}

export const DEFAULT_GUEST_ORDERING_SETTINGS: GuestOrderingSettings = {
  variant: GuestOrderingVariant.TABLE,
  minOrderValue: 10,
  paymentType: GuestOrderingPaymentType.BOTH,
  callWaiter: false,
  stations: [],
};

export type SMSRule = SmsSettingsDocument['reminder']['rules'][0];

export const DEFAULT_SMS_SETTINGS: SmsSettingsDocument = {
  reminder: {
    active: false,
    sendAt: 'morning',
    body: {
      de: 'Deine Reservierung steht heute an! Bitte bestätige uns dein kommen über den Link!',
      en: 'You have a reservation today please confirm us your visit via the link',
    }, // max count
    rules: [
      {
        active: false,
        name: 'Alle Gäste',
        all: true,
        ignoreOccassions: [],
        ignoreSources: [],
      },
      {
        active: true,
        name: 'An vollen Tagen',
        wDays: [5, 6],
        time: {
          from: '18:00',
          till: '22:00',
        },
        ignoreOccassions: [],
        ignoreSources: [],
      },
      {
        active: true,
        name: 'Große Gruppen',
        minGuests: 6,
        ignoreOccassions: [],
        ignoreSources: [],
      },
      {
        active: false,
        name: 'In Haus Reservierungen',
        ignoreOccassions: [],
        ignoreSources: ['phone', 'website'],
      },
    ],
  },
  confirmation: {
    active: false,
    body: {
      de: 'Vielen Dank für deine Reservierung. Bitte bestätige kurz über den Link das wir deine Angaben richtig verstanden haben.',
      en: 'Thank you for your Reservation. Please let us know if we got your details correctly.',
    }, // max count
    rules: [
      {
        active: true,
        name: 'In Haus Reservierungen',
        ignoreOccassions: [],
        ignoreSources: ['phone', 'website'],
      },
    ],
  },
  followup: {
    active: false,
    body: {
      de: 'Deine Meinung ist uns wichtig! Bitte lasse uns eine kurze Bewertung da wie dein Besuch war. Vielen Dank',
      en: 'Your feedback is important to us please let us know how you liked your stay. Thanks a lot',
    }, // max count
    rules: [
      {
        active: true,
        name: 'In Haus & SIA Reservierungen',
        ignoreOccassions: [],
        ignoreSources: ['website'],
      },
    ],
  },
};

export enum SIA_WELCOME_MESSAGE_TYPE {
  MESSAGE = 'message',
  AUDIO = 'audio',
  CONTACTPERSON = 'contactPerson',
}

export const DEFAULT_SIA_SETTINGS = {
  welcomeMessage: {
    type: SIA_WELCOME_MESSAGE_TYPE.MESSAGE,
    message: {
      de: 'Hallo, hier ist die ...',
      en: 'Hello here is the ...',
    },
  },
  openingHours: {
    regular: {
      '0': { close: '01:30', start: '22:30', kitchenClose: '' },
      '2': { start: '22:30', kitchenClose: '', close: '01:30' },
      '3': { close: '01:30', start: '22:30', kitchenClose: '' },
      '4': { close: '01:30', start: '22:30', kitchenClose: '' },
      '5': { start: '18:00', close: '01:30', kitchenClose: '22:00' },
      '6': { kitchenClose: '22:00', close: '01:30', start: '17:00' },
    },
    special: [
      {
        name: '',
        closed: true,
        hours: { close: '', kitchenClose: '', start: '' },
        date: '',
        msg: { en: '', de: '' },
      },
    ],
  },
  restaurantName: '',
  phoneBook: {
    Restaurant: { id: 'Restaurant', name: 'Restaurant', number: '+49' },
  },
  redirectStandard: { connectTo: 0, value: 'Restaurant' },
  faqs: {
    '0': {
      id: 'openingHours',
      action: {
        answer: {
          de: 'Derzeit haben wir donnerstags bis sonntags ab 18 Uhr geöffnet. Am Donnerstag und am Sonntag hat die Küche jeweils von 18:00 Uhr bis 22:00 Uhr für Sushi geöffnet. Von 22:00 Uhr bis 24:00 Uhr hat nur noch die Cocktailbar geöffnet. Am Freitag und am Samstag hat die Küche jeweils von 18:00 Uhr bis 23:00 Uhr für Sushi geöffnet. Von 23:00 Uhr bis 02:00 Uhr ist das Restaurant noch für Cocktails geöffnet. Kann Ich Ihnen sonst noch weiterhelfen? Eine neue Reservierung oder haben Sie allgemeine Fragen? ',
          en: 'Currently we are open Thursdays through Sundays from 6pm. On Thursday and Sunday the kitchen is open for sushi from 18:00 to 22:00. From 22:00 to 24:00 only the cocktail bar is open. On Friday and Saturday, the kitchen is open for sushi from 18:00 to 23:00 each day. From 23:00 to 02:00 the restaurant is still open for cocktails. Is there anything else I can help you with? A new reservation or do you have general questions? ',
        },
      },
    },
    '1': {
      id: 'corona',
      action: {
        answer: {
          en: 'Dear guests, due to the currently valid state regulation, non-vaccinated or not recovered guests must always present a negative rapid test. If you have not yet been vaccinated, we have created our own test capacities in store. Just contact us as soon as you enter the restaurant. Thank you very much!',
          de: 'Liebe Gäste, aufgrund der aktuell geltenden Landesverordnung müssen nicht geimpfte oder nicht genesene Gäste einen negativen Schnelltest stets vorlegen. Falls Sie noch nicht geimpft sind haben wir extra eigene Testkapazitäten vor Ort geschaffen. Sprechen Sie uns einfach an sobald Sie das Restaurant betreten. Vielen Dank!',
        },
      },
    },
    '2': {
      id: 'events',
      action: {
        answer: {
          de: 'Bitte wenden Sie sich für Event-Anfragen an einen unserer Ansprechpartner, soll ich Sie verbinden?',
          en: 'Please contact one of our contacts for event inquiries, shall I forward you?',
        },
      },
    },
    '3': {
      action: {
        answer: {
          en: 'You are very welcome to bring your dog. Would you like to book right away?',
          de: 'Sie dürfen sehr gerne Ihren Hund mitbringen. Möchten Sie gleich reservieren?',
        },
      },
      id: 'dog',
    },
  },
  fallbackMessage: {
    message: {
      en: 'We are currently not available please call us back Tuesday to Sunday between 18:00 and 23:00. Or write us an email to mail@schillingroofbar.com',
      de: 'Wir sind aktuell nicht erreichbar rufen Sie uns bitte Dienstag bis Sonntag zwischen 18:00 und 23:00 zurück. Oder schreiben Sie uns eine Email an mail@schillingroofbar.com',
    },
    type: 'message',
  },
  occassions: [],
  standard: { value: '', connectTo: 1 },
  shifts: [],
};
export const DEFAULT_MIN_CONS_SETTINGS: MinimumConsumptionSettingsDocument = {
  active: false,
  rules: [],
};

export const DEFAULT_FAQ_SETTINGS: FAQSettingsDocument = {
  topics: [],
  questions: [],
};

export type TicketCancellationTemplate = {
  id: string;
  name: string;
  calculation: TicketRefundCalculation;
  refundMethod: 'mollie' | 'voucher' | 'originalVoucher' | 'none';
  reason: string;
  /** Suggested in the cancel dialog once the event is at least this many days away. */
  minDaysBeforeEvent: number | null;
};

export type TicketCancellationSettings = {
  templates: TicketCancellationTemplate[];
};

export const DEFAULT_TICKET_CANCELLATION_SETTINGS: TicketCancellationSettings = {
  templates: [],
};
